import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import usePageTitle from "../hooks/usePageTitle";
import { trackEvent } from "../utils/analytics";
import { useTranslation, Trans } from "react-i18next";

function PartnersPage() {
  const { t } = useTranslation();
  usePageTitle(t("partners.pageTitle"), t("partners.pageSubtitle"));

  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [guildStats, setGuildStats] = useState({});

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

  // Busca de parceiros reais do Backend
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/partners`);
        if (!response.ok) throw new Error("Falha ao buscar parceiros");
        const data = await response.json();
        setPartners(data);

        // Buscar stats do Discord para cada guild_id
        data.forEach(async (partner) => {
          if (partner.guildId) {
            try {
              const res = await fetch(`https://discord.com/api/guilds/${partner.guildId}/widget.json`);
              if (res.ok) {
                const widgetData = await res.json();
                setGuildStats((prev) => ({
                  ...prev,
                  [partner.guildId]: {
                    presence_count: widgetData.presence_count,
                    members_count: widgetData.members ? widgetData.members.length : null,
                    // Note: widget.json doesn't always give total members if there are too many,
                    // but it gives presence_count (online).
                    // Actually, presence_count IS the online count.
                    // Discord Widget usually shows online count.
                    // For total members, we might need a different approach or just show online.
                  },
                }));
              }
            } catch (e) {
              console.error(`Erro ao buscar widget para ${partner.guildId}:`, e);
            }
          }
        });
      } catch (err) {
        console.error("Erro ao buscar parceiros:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPartners();
  }, [BACKEND_URL]);

  return (
    <div className="partners-page pb-5">
      <div className="hero-glow"></div>

      <section className="partners-hero">
        <div className="container text-center">
          <h1 className="display-3 fw-bold mb-4">
            <Trans
              i18nKey="partners.heroTitle"
              defaults="Nossos <span className='gradient-text'>Parceiros</span> Oficiais"
              components={{ span: <span className="gradient-text" /> }}
            />
          </h1>
          <p
            className="lead mx-auto mb-5"
            style={{
              maxWidth: "700px",
              color: "rgba(255,255,255,0.7)",
              fontSize: "1.2rem",
            }}
          >
            {t("partners.heroDescription")}
          </p>
        </div>
      </section>

      <section className="partners-grid-section pb-5">
        <div className="container">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t("partners.loading")}</span>
              </div>
            </div>
          ) : (
            <div className="partners-carousel-wrapper">
              <div className="carousel-fade-left"></div>
              <div className="carousel-fade-right"></div>

              <button
                className="carousel-arrow prev"
                onClick={() => {
                  const container = document.querySelector(".partners-carousel-container");
                  container.scrollBy({ left: -400, behavior: "smooth" });
                }}
                aria-label={t("partners.prev")}
              >
                <span>❮</span>
              </button>

              <div className="partners-carousel-container">
                {partners.map((partner) => (
                  <div key={partner.id} className="carousel-partner-item">
                    <div className="card partner-card text-center p-4 p-md-5 h-100 border-0">
                      <div className="partner-logo-container">
                        <img src={partner.logo} alt={partner.name} className="partner-logo" />
                      </div>
                      <span className="partner-type-badge">{partner.type}</span>
                      <h3 className="h4">{partner.name}</h3>
                      {guildStats[partner.guildId] && (
                        <div className="partner-members-stats mb-3">
                          <span className="online-indicator"></span>
                          <span className="members-count">
                            <strong>{guildStats[partner.guildId].presence_count.toLocaleString()}</strong> {t("partners.onlineMembers")}
                          </span>
                        </div>
                      )}
                      <p className="partner-description mb-4">{partner.description}</p>
                      <div className="mt-auto">
                        <a
                          href={partner.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-partner w-100"
                          onClick={() =>
                            trackEvent("partner_click", {
                              partner_name: partner.name,
                            })
                          }
                        >
                          {t("partners.meetPartner")}
                        </a>
                      </div>

                      {(partner.ownerName || partner.guildId) && (
                        <div className="partner-extra-info mt-4 pt-3 border-top border-white border-opacity-10">
                          {partner.ownerName && (
                            <div className="partner-owner d-flex align-items-center justify-content-center mb-2">
                              {partner.ownerAvatar ? (
                                <img
                                  src={`https://cdn.discordapp.com/avatars/${partner.ownerId}/${partner.ownerAvatar}.png`}
                                  alt={partner.ownerName}
                                  className="owner-avatar-mini me-2"
                                />
                              ) : (
                                <span className="owner-avatar-placeholder me-2">👤</span>
                              )}
                              <span className="owner-name-small">
                                {t("partners.ownerPrefix", "Dono:")} <strong>{partner.ownerName}</strong>
                              </span>
                            </div>
                          )}
                          {partner.guildId && (
                            <div className="partner-guild-id">
                              <span className="guild-id-badge">
                                {t("partners.guildIdPrefix")} {partner.guildId}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <button
                className="carousel-arrow next"
                onClick={() => {
                  const container = document.querySelector(".partners-carousel-container");
                  container.scrollBy({ left: 400, behavior: "smooth" });
                }}
                aria-label={t("partners.next")}
              >
                <span>❯</span>
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="be-a-partner-premium py-5 mt-5">
        <div className="container">
          <div className="partner-cta-box p-4 p-md-5">
            <div className="row align-items-center">
              <div className="col-lg-6 mb-5 mb-lg-0 text-center text-lg-start">
                <h2 className="display-5 fw-bold mb-4">
                  <Trans
                    i18nKey="partners.ctaTitle"
                    defaults="Faça parte da nossa <br /> <span className='gradient-text'>Elite de Parceiros</span>"
                    components={{
                      br: <br />,
                      span: <span className="gradient-text" />,
                    }}
                  />
                </h2>
                <p className="mb-5 opacity-75 fs-5">{t("partners.ctaDescription")}</p>
                <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center justify-content-lg-start">
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://discord.gg/xK4r9HqKKf"
                    className="btn btn-primary btn-lg px-5 py-3 rounded-pill fw-bold"
                    onClick={() => trackEvent("partner_apply_click")}
                  >
                    {t("partners.ctaButton")}
                  </a>
                </div>
              </div>
              <div className="col-lg-6 ps-lg-5">
                <div className="row g-3">
                  <div className="col-12">
                    <div className="benefit-pill">
                      <div className="d-flex align-items-center">
                        <span className="fs-3 me-3">🌐</span>
                        <div>
                          <h5 className="mb-1 fw-bold">{t("partners.benefit1Title")}</h5>
                          <p className="small mb-0 opacity-50">{t("partners.benefit1Desc")}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="benefit-pill">
                      <div className="d-flex align-items-center">
                        <span className="fs-3 me-3">✅</span>
                        <div>
                          <h5 className="mb-1 fw-bold">{t("partners.benefit2Title")}</h5>
                          <p className="small mb-0 opacity-50">{t("partners.benefit2Desc")}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="benefit-pill">
                      <div className="d-flex align-items-center">
                        <span className="fs-3 me-3">⚡</span>
                        <div>
                          <h5 className="mb-1 fw-bold">{t("partners.benefit3Title")}</h5>
                          <p className="small mb-0 opacity-50">{t("partners.benefit3Desc")}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default PartnersPage;
